import { useState, useEffect } from 'react';
import { platformsAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  ExternalLink, Link2, Pencil, Trash2, Check, X, Loader2, Globe,
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Brand-colored backgrounds for each platform
const PLATFORM_COLORS = {
  instagram: 'from-pink-500 via-purple-500 to-orange-400',
  tiktok: 'from-zinc-900 to-zinc-700',
  youtube: 'from-red-600 to-red-500',
  snapchat: 'from-yellow-400 to-yellow-300',
  pinterest: 'from-red-600 to-red-500',
  linkedin: 'from-blue-700 to-blue-600',
  facebook: 'from-blue-600 to-blue-500',
  meta_business: 'from-blue-600 to-indigo-600',
  google_business: 'from-blue-500 via-green-500 to-yellow-500',
  tiktok_ads: 'from-cyan-500 to-zinc-800',
  pinterest_ads: 'from-red-500 to-red-600',
  etsy: 'from-orange-500 to-orange-600',
  ebay: 'from-blue-600 via-red-500 to-yellow-400',
  amazon: 'from-amber-500 to-amber-600',
  whatnot: 'from-violet-600 to-violet-500',
  fb_marketplace: 'from-blue-500 to-blue-600',
  yelp: 'from-red-600 to-red-500',
  the_knot: 'from-teal-600 to-teal-500',
  wedding_wire: 'from-amber-600 to-amber-500',
  trustpilot: 'from-green-600 to-green-500',
  zola: 'from-zinc-800 to-zinc-700',
  reddit: 'from-orange-600 to-orange-500',
};

const PLATFORM_INITIALS = {
  instagram: 'IG',
  tiktok: 'TT',
  youtube: 'YT',
  snapchat: 'SC',
  pinterest: 'Pi',
  linkedin: 'Li',
  facebook: 'Fb',
  meta_business: 'M',
  google_business: 'G',
  tiktok_ads: 'TT',
  pinterest_ads: 'Pi',
  etsy: 'Et',
  ebay: 'eB',
  amazon: 'Az',
  whatnot: 'Wn',
  fb_marketplace: 'Fb',
  yelp: 'Ye',
  the_knot: 'TK',
  wedding_wire: 'WW',
  trustpilot: 'Tp',
  zola: 'Zo',
  reddit: 'Re',
};

const CATEGORY_ICONS = {
  'Social Media': '📱',
  'Business / Ads': '📊',
  'Marketplaces': '🏪',
  'Reviews / Wedding': '💍',
  'Community': '💬',
};

const PlatformCard = ({ platform, savedLink, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState(savedLink?.url || '');
  const [saving, setSaving] = useState(false);

  const hasLink = savedLink?.url;
  const gradient = PLATFORM_COLORS[platform.key] || 'from-zinc-600 to-zinc-500';
  const initials = PLATFORM_INITIALS[platform.key] || platform.name[0];

  const handleSave = async () => {
    if (!url.trim()) return;
    setSaving(true);
    try {
      await onSave(platform.key, url.trim());
      setEditing(false);
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    await onDelete(platform.key);
    setUrl('');
  };

  return (
    <div
      className={`group relative rounded-xl border transition-all duration-200 overflow-hidden ${
        hasLink
          ? 'border-zinc-200 bg-white hover:shadow-md hover:border-zinc-300'
          : 'border-dashed border-zinc-200 bg-zinc-50/50 hover:bg-white hover:border-zinc-300'
      }`}
      data-testid={`platform-card-${platform.key}`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Platform Logo */}
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-sm`}>
            {platform.icon ? (
              <img
                src={platform.icon}
                alt=""
                className="w-6 h-6 object-contain"
                loading="lazy"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : null}
            {!platform.icon && (
              <span className="text-white text-xs font-bold">{initials}</span>
            )}
          </div>

          {/* Name + Status */}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-zinc-900 leading-tight">{platform.name}</h3>
            {hasLink && !editing ? (
              <a
                href={savedLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 truncate block mt-0.5 max-w-[180px]"
                data-testid={`platform-link-${platform.key}`}
              >
                {savedLink.url.replace(/^https?:\/\//, '').slice(0, 35)}
                {savedLink.url.replace(/^https?:\/\//, '').length > 35 ? '...' : ''}
              </a>
            ) : !editing ? (
              <p className="text-xs text-zinc-400 mt-0.5">No link added</p>
            ) : null}
          </div>

          {/* Actions */}
          {!editing && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {hasLink && (
                <a href={savedLink.url} target="_blank" rel="noopener noreferrer"
                  className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500 hover:text-blue-600 transition-colors"
                  data-testid={`open-platform-${platform.key}`}>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              <button onClick={() => { setUrl(savedLink?.url || ''); setEditing(true); }}
                className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors"
                data-testid={`edit-platform-${platform.key}`}>
                {hasLink ? <Pencil className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
              </button>
              {hasLink && (
                <button onClick={handleDelete}
                  className="p-1.5 rounded-md hover:bg-red-50 text-zinc-400 hover:text-red-500 transition-colors"
                  data-testid={`delete-platform-${platform.key}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Edit Mode */}
        {editing && (
          <div className="mt-3 flex items-center gap-2">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
              autoFocus
              data-testid={`platform-url-input-${platform.key}`}
              className="flex-1 text-xs px-3 py-2 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-200 focus:border-zinc-300 bg-white"
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
            />
            <button onClick={handleSave} disabled={saving || !url.trim()}
              className="p-2 rounded-lg bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-40 transition-colors"
              data-testid={`save-platform-${platform.key}`}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => setEditing(false)}
              className="p-2 rounded-lg border border-zinc-200 text-zinc-500 hover:bg-zinc-50 transition-colors"
              data-testid={`cancel-platform-${platform.key}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Connected indicator bar */}
      {hasLink && (
        <div className={`h-0.5 bg-gradient-to-r ${gradient}`} />
      )}
    </div>
  );
};

const Platforms = () => {
  const [catalog, setCatalog] = useState({});
  const [savedLinks, setSavedLinks] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catalogRes, linksRes] = await Promise.all([
          platformsAPI.getCatalog(),
          platformsAPI.getLinks(),
        ]);
        setCatalog(catalogRes.data || {});
        const linksMap = {};
        (linksRes.data || []).forEach(l => { linksMap[l.key] = l; });
        setSavedLinks(linksMap);
      } catch { toast.error('Failed to load platforms'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleSave = async (key, url) => {
    try {
      const r = await platformsAPI.saveLink({ key, url });
      setSavedLinks(prev => ({ ...prev, [key]: r.data }));
      toast.success('Link saved');
    } catch { toast.error('Failed to save link'); }
  };

  const handleDelete = async (key) => {
    try {
      await platformsAPI.deleteLink(key);
      setSavedLinks(prev => { const n = { ...prev }; delete n[key]; return n; });
      toast.success('Link removed');
    } catch { toast.error('Failed to remove link'); }
  };

  const connectedCount = Object.keys(savedLinks).length;
  const totalCount = Object.values(catalog).flat().length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="platforms-loading">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 fade-in" data-testid="platforms-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="platforms-title">
            Platforms
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {connectedCount} of {totalCount} platforms linked
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full">
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-xs font-medium text-emerald-700">{connectedCount} connected</span>
        </div>
      </div>

      {/* Categories */}
      {Object.entries(catalog).map(([category, platforms]) => {
        const catConnected = platforms.filter(p => savedLinks[p.key]).length;
        return (
          <div key={category} data-testid={`platform-category-${category.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg">{CATEGORY_ICONS[category] || '📌'}</span>
              <h2 className="text-base font-semibold text-zinc-800">{category}</h2>
              <span className="text-xs text-zinc-400 ml-1">
                {catConnected}/{platforms.length}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {platforms.map(platform => (
                <PlatformCard
                  key={platform.key}
                  platform={platform}
                  savedLink={savedLinks[platform.key]}
                  onSave={handleSave}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Platforms;
